<template>
  <div class="products-page">
    <!-- ========== 顶部导航 ========== -->
    <AppHeader />

    <!-- ========== 英雄区 ========== -->
    <section class="hero-section product-hero">
      <div class="hero-content">
        <div class="hero-badge">
          <span class="badge-dot"></span>
          2026 全系产品矩阵
        </div>
        <h1 class="hero-title">
          工业物联<br />智能终端
        </h1>
        <p class="hero-desc">
          涵盖工业路由器、边缘网关、数传DTU及通信模组，<br />
          支持 5G/4G/Wi-Fi 6/LoRa 全协议栈，赋能设备高效互联
        </p>
        <div class="hero-actions">
          <el-button type="primary" size="large" round class="hero-btn-primary" @click="handleGetSolution">
            定制设备
            <el-icon><ArrowRight /></el-icon>
          </el-button>
          <el-button size="large" class="hero-btn-ghost" round @click="handleConsult">
            联系我们
          </el-button>
        </div>
        <div class="hero-stats">
          <div class="stat-item">
            <span class="stat-number">200+</span>
            <span class="stat-label">在售型号</span>
          </div>
          <div class="stat-divider"></div>
          <div class="stat-item">
            <span class="stat-number">30+</span>
            <span class="stat-label">全球认证</span>
          </div>
          <div class="stat-divider"></div>
          <div class="stat-item">
            <span class="stat-number">7×24h</span>
            <span class="stat-label">稳定运行</span>
          </div>
        </div>
      </div>
      <div class="hero-visual">
        <div class="hero-orb"></div>
        <div class="hero-orb-secondary"></div>
        <div class="hero-grid-pattern"></div>
        <div class="hero-device-mockup">
          <el-icon :size="64"><Cpu /></el-icon>
        </div>
      </div>
    </section>

    <!-- ========== 搜索栏 ========== -->
    <div class="search-bar-wrapper">
      <div class="search-bar-inner">
        <el-input v-model="searchKeyword" placeholder="搜索产品型号、名称、描述..." prefix-icon="Search" size="large"
          class="search-input" clearable @keyup.enter="handleSearch" />
        <el-button type="primary" size="large" @click="handleSearch">
          <el-icon><Search /></el-icon> 搜索
        </el-button>
      </div>
    </div>

    <!-- ========== 分类区块 + 左侧筛选栏 ========== -->
    <div class="category-sections-wrapper">
      <!-- 左侧筛选栏（仅大类） -->
      <aside class="filter-sidebar-sticky" v-if="rootCategories.length">
        <div class="filter-card">
          <div class="filter-title">产品分类</div>
          <div class="filter-list">
            <button v-for="cat in rootCategories" :key="cat.id" class="filter-item" @click="scrollToCategory(cat.code)">
              <span class="filter-dot" :style="{ background: getCategoryColor(cat.code) }"></span>
              {{ cat.name }}
            </button>
          </div>
        </div>
      </aside>

      <!-- 右侧分类区块列表 -->
    <div class="category-sections">
      <div v-for="cat in rootCategories" :key="cat.id" class="category-block" :id="'block-' + cat.code">
        <!-- 左侧主视觉区（大类） -->
        <div class="block-header" :style="{ background: getBlockHeaderMeta(cat).gradient }">
          <div class="header-content">
            <h2 class="block-title">{{ getBlockHeaderMeta(cat).title }}</h2>
            <p class="block-desc">{{ getBlockHeaderMeta(cat).desc }}</p>

          </div>
        </div>

        <!-- 右侧（小类筛选 + 产品网格） -->
        <div class="block-right">
          <!-- 小类筛选栏 -->
          <div class="sub-category-filter" v-if="getChildren(cat.id).length">
            <span class="filter-label">分类：</span>
            <button
              class="sub-filter-btn"
              :class="{ active: getBlockState(cat.id)?.selectedSubCategory === null }"
              @click="selectSubCategory(cat.id, null)"
            >
              全部
            </button>
            <button
              v-for="child in getChildren(cat.id)"
              :key="child.id"
              class="sub-filter-btn"
              :class="{ active: getBlockState(cat.id)?.selectedSubCategory === child.code }"
              @click="selectSubCategory(cat.id, child.code)"
            >
              {{ child.name }}
            </button>
          </div>

          <!-- 产品网格（每行3个） -->
          <div class="block-products">
            <div
              v-for="product in getBlockState(cat.id)?.products || []"
              :key="product.id"
              class="product-card-lg"
              @click="goToDetail(product.id)"
            >
              <div class="card-img-wrap">
                <el-image :src="getProductImage(product)" fit="cover" class="card-img" lazy>
                  <template #placeholder>
                    <div class="img-placeholder">
                      <el-icon :size="32"><Monitor /></el-icon>
                    </div>
                  </template>
                </el-image>
                <div class="card-badge" v-if="product.tag">
                  <span>{{ product.tag }}</span>
                </div>
              </div>
              <div class="card-body">
                <h3 class="card-name">{{ product.name }}</h3>
                <p class="card-model">{{ product.model }}</p>
                <p class="card-desc">{{ product.description || product.desc || '' }}</p>
                <div class="card-specs">
                  <span
                    v-for="(spec, idx) in (product.specs || []).slice(0, 2)"
                    :key="idx"
                    class="spec-tag"
                  >
                    {{ spec }}
                  </span>
                </div>
              </div>
              <div class="card-footer">
                <el-button text size="small" @click.stop="addCompare(product.id)">
                  <el-icon><DataLine /></el-icon> 对比
                </el-button>
              </div>
            </div>

            <!-- 空状态 -->
            <div
              v-if="!getBlockState(cat.id)?.loading && getBlockState(cat.id)?.products?.length === 0"
              class="empty-product-tip"
            >
              暂无该分类下的产品
            </div>
          </div>

          <!-- 加载更多 -->
          <div class="load-more-wrap" v-if="getBlockState(cat.id)?.hasMore">
            <el-button type="primary" plain :loading="getBlockState(cat.id)?.loadingMore" @click="loadMore(cat.id)">
              加载更多
            </el-button>
          </div>
        </div>
      </div>
    </div>
</div>
    <!-- ========== 特色推荐区 ========== -->
    <!-- <section class="featured-section">
      <div class="featured-inner">
        <div class="featured-info">
          <div class="featured-label">
            <span class="label-line"></span>
            精选物联网设备
          </div>
          <h2>{{ featuredProduct?.name || '加载中...' }}</h2>
          <p>{{ featuredProduct?.desc || '' }}</p>
          <ul class="featured-features" v-if="featuredProduct?.features">
            <li v-for="(f, i) in featuredProduct.features" :key="i">
              <el-icon><Check /></el-icon> {{ f }}
            </li>
          </ul>
          <button class="featured-btn" @click="goToDetail(featuredProduct?.id)">
            了解详情
            <el-icon><ArrowRight /></el-icon>
          </button>
        </div>
        <div class="featured-visual">
          <div class="featured-glow"></div>
          <div class="featured-icon-wrap">
            <el-icon :size="72"><Cpu /></el-icon>
          </div>
        </div>
      </div>
    </section> -->
  </div>
</template>

<script setup>
import { ref, reactive, computed, onMounted, watch, nextTick } from 'vue'
import { useRouter, useRoute } from 'vue-router'
import { ElMessage } from 'element-plus'
import {
  Monitor,
  Search,
  DataLine,
  Cpu,
  VideoPlay,
  Check,
  ArrowRight,
} from '@element-plus/icons-vue'
import AppHeader from '@/components/AppHeader.vue'
import { getProducts, getProductMatrix } from '@/api/product'

const router = useRouter()
const route = useRoute()

// ========== 状态 ==========
const searchKeyword = ref('')
const categories = ref([])        // 铺平的分类（仅供配色/标题/搜索等查找使用）
const matrix = ref([])            // 后端聚合好的分类树（大类→小类，含首屏产品与总数）
const matrixMap = reactive({})    // 大类 id → { products, total }，后端首屏数据
const featuredProduct = ref(null)
const loading = ref(false)

// 每个大类的运行时数据状态（以大类 id 为 key）
const blocksData = reactive({})

// ========== 分类树辅助（直接消费后端返回的树，前端不再自行建树） ==========
const rootCategories = computed(() => matrix.value)

const getChildren = (parentId) => {
  return matrix.value.find(n => n.id === parentId)?.children || []
}

const getBlockState = (categoryId) => {
  if (!blocksData[categoryId]) {
    blocksData[categoryId] = {
      products: [],
      page: 1,
      pageSize: 6,
      total: 0,
      loading: false,
      loadingMore: false,
      hasMore: false,
      selectedSubCategory: null,
    }
  }
  return blocksData[categoryId]
}

// ========== 动态分类配色（基于数据库分类数据） ==========
const CATEGORY_PALETTE = [
  {
    dotColor: '#1E56C9',
    bgGradient: 'linear-gradient(135deg, #0A2E7D, #1E56C9)',
  },
  {
    dotColor: '#FF8C42',
    bgGradient: 'linear-gradient(135deg, #FF6B1F, #FF8C42)',
  },
  {
    dotColor: '#7C3AED',
    bgGradient: 'linear-gradient(135deg, #4F46E5, #7C3AED)',
  },
  {
    dotColor: '#34D399',
    bgGradient: 'linear-gradient(135deg, #10B981, #34D399)',
  },
  {
    dotColor: '#8B5CF6',
    bgGradient: 'linear-gradient(135deg, #8B5CF6, #A78BFA)',
  },
  {
    dotColor: '#3B82F6',
    bgGradient: 'linear-gradient(135deg, #3B82F6, #60A5FA)',
  },
  {
    dotColor: '#F59E0B',
    bgGradient: 'linear-gradient(135deg, #F59E0B, #FBBF24)',
  },
  {
    dotColor: '#EC4899',
    bgGradient: 'linear-gradient(135deg, #EC4899, #F472B6)',
  },
]

const getCategoryPaletteIndex = (categoryOrCode) => {
  const source = typeof categoryOrCode === 'string'
    ? categoryOrCode
    : `${categoryOrCode?.code || ''}${categoryOrCode?.sortOrder || ''}`

  const hash = Array.from(source).reduce((acc, char) => acc + char.charCodeAt(0), 0)
  return hash % CATEGORY_PALETTE.length
}

const getCategoryColor = (categoryOrCode) => {
  return CATEGORY_PALETTE[getCategoryPaletteIndex(categoryOrCode)]?.dotColor || '#6B7280'
}

const getCategoryGradient = (categoryOrCode) => {
  return CATEGORY_PALETTE[getCategoryPaletteIndex(categoryOrCode)]?.bgGradient || 'linear-gradient(135deg, #6B7280, #9CA3AF)'
}

const getBlockHeaderMeta = (category) => {
  const state = getBlockState(category.id)
  const selectedCategory = state.selectedSubCategory
    ? categories.value.find(item => item.code === state.selectedSubCategory)
    : null

  const activeCategory = selectedCategory || category

  return {
    title: activeCategory?.name || category?.name || '',
    desc: activeCategory?.description || category?.description || '',
    gradient: getCategoryGradient(category),
    dotColor: getCategoryColor(category),
  }
}

// ========== 加载产品矩阵（后端一次性聚合分类树 + 各大类首屏产品，前端无需多次请求） ==========
const loadMatrix = async () => {
  try {
    const res = await getProductMatrix(6)
    const list = Array.isArray(res?.data)
      ? res.data
      : (Array.isArray(res) ? res : [])

    matrix.value = list.filter(Boolean)

    // 铺平分类（标注 parentId）供配色/标题/搜索查找；记录各大类首屏产品与总数
    const flat = []
    for (const root of matrix.value) {
      flat.push({ ...root, parentId: null })
      for (const child of (root.children || [])) {
        flat.push({ ...child, parentId: root.id })
      }
      matrixMap[root.id] = { products: root.products || [], total: root.total || 0 }
    }
    categories.value = flat
  } catch (error) {
    console.error('加载产品矩阵失败:', error)
    ElMessage.error('加载产品失败，请刷新重试')
  }
}

// ========== 商品图片兼容 ==========
const getProductImage = (product) => {
  const candidates = [
    product?.image,
    product?.img,
    product?.imageUrl,
    product?.cover,
    product?.thumbnail,
    product?.picture,
    product?.avatar,
    product?.images?.[0],
    product?.photos?.[0],
    product?.imageList?.[0],
  ]

  for (const candidate of candidates) {
    if (typeof candidate === 'string' && candidate) return candidate
    if (candidate && typeof candidate === 'object') {
      const nested = candidate.url || candidate.src || candidate.path || candidate.link
      if (typeof nested === 'string' && nested) return nested
    }
  }

  return ''
}

// ========== 商品列表兼容 ==========
const extractProductsList = (response) => {
  const payload = response?.data
  if (Array.isArray(payload)) return payload
  if (Array.isArray(payload?.list)) return payload.list
  if (Array.isArray(payload?.items)) return payload.items
  if (Array.isArray(payload?.rows)) return payload.rows
  return []
}

const extractProductsTotal = (response) => {
  const payload = response?.data
  if (typeof payload?.total === 'number') return payload.total
  if (typeof payload?.totalCount === 'number') return payload.totalCount
  return extractProductsList(response).length
}

// ========== 加载某个 block 的商品数据 ==========
const loadBlockData = async (categoryId, subCategoryCode, page = 1, pageSize = 6, append = false) => {
  const state = getBlockState(categoryId)
  if (state.loading) return

  const cat = categories.value.find(c => c.id === categoryId)
  if (!cat) return

  state.loading = true
  try {
    const params = {
      page,
      pageSize,
    }

    // 如果选中的是小类，就按小类 code 查询；否则按大类 code 查询
    params.categoryCode = subCategoryCode || cat.code

    const res = await getProducts(params)
    const list = extractProductsList(res)
    const total = extractProductsTotal(res)

    if (append) {
      state.products = [...state.products, ...list]
    } else {
      state.products = list
    }

    state.total = total
    state.page = page
    state.hasMore = state.products.length < total
  } catch (error) {
    console.error('加载商品失败:', error)
    ElMessage.error('加载商品失败，请稍后重试')
    if (!append) {
      state.products = []
      state.total = 0
      state.hasMore = false
    }
  } finally {
    state.loading = false
    state.loadingMore = false
  }
}

// ========== 切换子类 ==========
const selectSubCategory = (categoryId, subCategoryCode) => {
  const state = getBlockState(categoryId)
  if (state.selectedSubCategory === subCategoryCode) return
  state.selectedSubCategory = subCategoryCode
  state.page = 1
  state.products = []
  state.total = 0
  state.hasMore = false
  loadBlockData(categoryId, subCategoryCode, 1, state.pageSize, false)
}

// ========== 加载更多 ==========
const loadMore = (categoryId) => {
  const state = getBlockState(categoryId)
  if (state.loadingMore || !state.hasMore) return
  state.loadingMore = true
  const nextPage = state.page + 1
  loadBlockData(categoryId, state.selectedSubCategory, nextPage, state.pageSize, true)
}

// ========== 用矩阵数据初始化各大类展示状态（无需再逐类请求） ==========
const initAllBlocks = () => {
  if (!matrix.value.length) return

  for (const cat of rootCategories.value) {
    const m = matrixMap[cat.id] || { products: [], total: 0 }
    const state = getBlockState(cat.id)
    state.selectedSubCategory = null
    state.page = 1
    state.products = m.products
    state.total = m.total
    state.hasMore = state.products.length < state.total
  }

  const firstCat = rootCategories.value[0]
  if (firstCat) {
    const state = getBlockState(firstCat.id)
    if (state.products.length) {
      featuredProduct.value = state.products[0]
    }
  }
}

// ========== 搜索处理 ==========
const handleSearch = async () => {
  const keyword = searchKeyword.value.trim()
  if (!keyword) {
    window.scrollTo({ top: 0, behavior: 'smooth' })
    return
  }

  try {
    const res = await getProducts({ keyword, page: 1, pageSize: 100 })
    const list = extractProductsList(res)
    if (list.length) {
      const matched = list[0]
      const categoryCode = matched.category
      if (categoryCode) {
        const subCat = categories.value.find(c => c.code === categoryCode)
        let parentId = subCat?.parentId
        let targetId = parentId
        if (!targetId) {
          const root = rootCategories.value.find(c => c.code === categoryCode)
          if (root) targetId = root.id
        }
        if (targetId) {
          const element = document.getElementById('block-' + (categories.value.find(c => c.id === targetId)?.code || ''))
          if (element) {
            element.scrollIntoView({ behavior: 'smooth', block: 'start' })
            if (subCat && subCat.parentId) {
              selectSubCategory(targetId, subCat.code)
            }
            return
          }
        }
      }
      ElMessage.info(`找到商品：${matched.name}，但无法定位分类`)
    } else {
      ElMessage.info(`未找到与“${keyword}”匹配的产品`)
    }
  } catch {
    ElMessage.error('搜索失败，请重试')
  }
}

// ========== 滚动到指定分类 ==========
const scrollToCategory = (code) => {
  const element = document.getElementById('block-' + code);
  if (element) {
    const yOffset = -180; // 负数=往上偏移，数值根据需要调整
    const y = element.getBoundingClientRect().top + window.pageYOffset + yOffset;
    window.scrollTo({ top: y, behavior: 'smooth' });
  }
}
// ========== 跳转详情 ==========
const goToDetail = (id) => {
  if (id) router.push(`/product/${id}`)
}

// ========== 加入对比 ==========
const addCompare = (id) => {
  ElMessage.success('该功能正在努力研发中')
}

// ========== 操作按钮 ==========
const handleGetSolution = () => {
  ElMessage.success('正在为您匹配最佳方案...')
}
const handleConsult = () => {
  ElMessage.success('已提交预约咨询，客服将尽快与您联系')
}

// ========== 生命周期 ==========
onMounted(async () => {
  await loadMatrix()
  initAllBlocks()
  // 支持首页/页脚带分类参数直达：/products?category=xxx
  const catCode = route.query.category
  if (catCode) {
    await nextTick()
    scrollToCategory(catCode)
  }
})

// 页面已打开时再次点击带分类的链接，也能滚动到对应区块
watch(() => route.query.category, async (code) => {
  if (code) {
    await nextTick()
    scrollToCategory(code)
  }
})
</script>

<style scoped>
/* ========== 页面基础 ========== */
.products-page {
  padding-top: 72px;
  background: #f6f8fc;
  min-height: 100vh;
  font-family: -apple-system, 'PingFang SC', 'Microsoft YaHei', sans-serif;
}

/* ========== 英雄区 ========== */
.hero-section {
  display: flex;
  align-items: center;
  justify-content: space-between;
  max-width: 1440px;
  margin: 0 auto;
  gap: 80px;
  min-height: 520px;
  position: relative;
}

.hero-content {
  flex: 1;
  z-index: 2;
}

.hero-badge {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  background: rgba(79, 70, 229, 0.08);
  color: #4F46E5;
  padding: 8px 20px 8px 14px;
  border-radius: 100px;
  font-size: 14px;
  font-weight: 500;
  margin-bottom: 24px;
}

.badge-dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: #4F46E5;
  animation: pulse-dot 2s ease-in-out infinite;
}

@keyframes pulse-dot {
  0%, 100% { opacity: 1; transform: scale(1); }
  50% { opacity: 0.4; transform: scale(0.8); }
}

.hero-title {
  font-size: 64px;
  font-weight: 700;
  line-height: 1.08;
  color: #0a0a1a;
  margin: 0 0 20px 0;
  letter-spacing: -2px;
}
.hero-actions {
  display: flex;
  gap: 16px;
  flex-wrap: wrap;
  margin-bottom: 28px;
}
.hero-desc {
  font-size: 18px;
  color: #6b7280;
  line-height: 1.8;
  margin: 0 0 36px 0;
  max-width: 500px;
}

.hero-stats {
  display: flex;
  align-items: center;
  gap: 32px;
}

.stat-item {
  display: flex;
  flex-direction: column;
}

.stat-number {
  font-size: 28px;
  font-weight: 700;
  color: #0a0a1a;
  letter-spacing: -0.5px;
}

.stat-label {
  font-size: 14px;
  color: #9ca3af;
  margin-top: 2px;
}

.stat-divider {
  width: 1px;
  height: 40px;
  background: #e5e7eb;
}

.hero-visual {
  flex: 0 0 400px;
  height: 400px;
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
}

.hero-orb {
  position: absolute;
  width: 360px;
  height: 360px;
  border-radius: 50%;
  background: radial-gradient(circle at 30% 30%, rgba(79, 70, 229, 0.15), rgba(79, 70, 229, 0.02));
  animation: float-orb 8s ease-in-out infinite;
}

.hero-orb-secondary {
  position: absolute;
  width: 240px;
  height: 240px;
  border-radius: 50%;
  background: radial-gradient(circle at 70% 70%, rgba(16, 185, 129, 0.1), transparent);
  animation: float-orb 10s ease-in-out infinite reverse;
}

@keyframes float-orb {
  0%, 100% { transform: translate(0, 0) scale(1); }
  50% { transform: translate(-12px, -20px) scale(1.05); }
}

.hero-grid-pattern {
  position: absolute;
  inset: 0;
  background-image:
    linear-gradient(rgba(79, 70, 229, 0.04) 1px, transparent 1px),
    linear-gradient(90deg, rgba(79, 70, 229, 0.04) 1px, transparent 1px);
  background-size: 40px 40px;
  border-radius: 50%;
}

.hero-device-mockup {
  position: relative;
  z-index: 2;
  width: 140px;
  height: 140px;
  border-radius: 28px;
  background: rgba(255, 255, 255, 0.6);
  backdrop-filter: blur(20px);
  border: 1px solid rgba(255, 255, 255, 0.8);
  box-shadow: 0 20px 60px rgba(10, 10, 26, 0.08);
  display: flex;
  align-items: center;
  justify-content: center;
  color: #4F46E5;
}

.product-hero .hero-orb {
  background: radial-gradient(circle, rgba(59, 130, 246, 0.25) 0%, transparent 70%);
}

.product-hero .hero-badge {
  background: rgba(59, 130, 246, 0.1);
  border-color: rgba(59, 130, 246, 0.2);
}

.product-hero .badge-dot {
  background: #3b82f6;
  box-shadow: 0 0 8px rgba(59, 130, 246, 0.6);
}

/* ====== 新增按钮样式（与 Solutions.vue 一致） ====== */
.hero-btn-primary {
  background: #0a0a1a !important;
  border: none !important;
  padding: 14px 40px !important;
  font-weight: 500;
  font-size: 16px !important;
  transition: all 0.3s ease;
}

.hero-btn-primary:hover {
  background: #2a2a4a !important;
  transform: translateY(-2px);
  box-shadow: 0 12px 32px rgba(10, 10, 26, 0.2);
}

.hero-btn-ghost {
  border: 1.5px solid #d1d5db !important;
  background: transparent !important;
  color: #1a1a2e !important;
  padding: 14px 40px !important;
  font-weight: 500;
  font-size: 16px !important;
  transition: all 0.3s ease;
}

.hero-btn-ghost:hover {
  border-color: #0a0a1a !important;
  background: rgba(10, 10, 26, 0.04) !important;
  transform: translateY(-2px);
}

/* ========== 搜索栏 ========== */
.search-bar-wrapper {
  position: sticky;
  top: 80px;
  z-index: 10;
  max-width: 1000px;
  margin: 0 auto;
  padding: 16px 40px 0;
}

.search-bar-inner {
  display: flex;
  gap: 12px;
  background: #fff;
  padding: 12px 16px;
  border-radius: 16px;
  border: 1px solid #eef2f6;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.02);
}

.search-bar-inner .search-input {
  flex: 1;
}

.search-bar-inner .search-input :deep(.el-input__wrapper) {
  border-radius: 8px;
  background: #f5f7fb;
  border: 1px solid transparent;
  transition: all 0.3s ease;
  box-shadow: none !important;
}

.search-bar-inner .search-input :deep(.el-input__wrapper:hover) {
  border-color: #d1d5db;
}

.search-bar-inner .search-input :deep(.el-input__wrapper.is-focus) {
  border-color: #4F46E5;
  background: #fff;
  box-shadow: 0 0 0 3px rgba(79, 70, 229, 0.08) !important;
}

.search-bar-inner .el-button {
  border-radius: 8px;
  padding: 0 28px;
  font-weight: 500;
}

/* ========== 分类区块 + 左侧筛选栏 ========== */
.category-sections-wrapper {
  max-width: 75%;
  margin: 24px auto 0;
  padding: 0 40px 40px;
  display: flex;
  gap: 32px;
  align-items: flex-start;
}

.filter-sidebar-sticky {
  width: 240px;
  flex-shrink: 0;
  position: sticky;
  top: 96px;
  align-self: flex-start;
}

.filter-card {
  background: #ffffff;
  border-radius: 20px;
  padding: 28px 24px;
  border: 1px solid #eef2f6;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.04);
}

.filter-title {
  font-size: 20px;
  font-weight: 700;
  color: #0a0a1a;
  margin-bottom: 20px;
  padding-bottom: 14px;
  border-bottom: 2px solid #f0f2f5;
  letter-spacing: 0.3px;
}

.filter-list {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.filter-item {
  display: flex;
  align-items: center;
  gap: 14px;
  padding: 12px 16px;
  border: none;
  background: transparent;
  color: #4b5563;
  font-size: 16px;
  font-weight: 500;
  cursor: pointer;
  border-radius: 12px;
  transition: all 0.25s ease;
  font-family: inherit;
  width: 100%;
  text-align: left;
  line-height: 1.5;
}

.filter-item:hover {
  background: #f3f4f6;
  color: #1a1a2e;
  transform: translateX(4px);
}

.filter-dot {
  width: 12px;
  height: 12px;
  border-radius: 50%;
  flex-shrink: 0;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
}

/* 右侧分类区块列表 */
.category-sections {
  flex: 1;
  min-width: 0;
}

.category-block {
  display: flex;
  gap: 32px;
  margin-bottom: 10px;
  border-radius: 16px;
  overflow: hidden;
  box-shadow: 0 12px 32px rgba(0, 0, 0, 0.06);
  background: #fff;
}

.block-header {
  width: 280px;
  min-width: 280px;
  min-height: 400px;
  padding: 40px 28px;
  color: white;
  position: relative;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
}

.block-header:before {
  content: "";
  position: absolute;
  top: -50%;
  right: -50%;
  width: 500px;
  height: 500px;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.08);
  z-index: 0;
}

.header-content {
  position: relative;
  z-index: 2;
}

.block-title {
  font-size: 28px;
  font-weight: 700;
  margin: 0 0 12px 0;
  line-height: 1.2;
}

.block-desc {
  font-size: 15px;
  opacity: 0.9;
  max-width: 240px;
  margin-bottom: 24px;
  line-height: 1.6;
}

.play-btn {
  position: absolute;
  bottom: 32px;
  right: 32px;
  width: 56px;
  height: 56px;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.2);
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.3s ease;
}

.play-btn .el-icon {
  font-size: 28px;
  color: white;
}

.play-btn:hover {
  background: rgba(255, 255, 255, 0.3);
  transform: scale(1.05);
}

.block-right {
  flex: 1;
  display: flex;
  flex-direction: column;
  padding: 20px 20px 20px 0;
  min-width: 0;
}

.sub-category-filter {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 8px 12px;
  padding: 8px 0 16px 0;
  border-bottom: 1px solid #f0f2f5;
  margin-bottom: 16px;
}

.sub-category-filter .filter-label {
  font-size: 14px;
  font-weight: 500;
  color: #6b7280;
  margin-right: 4px;
}

.sub-filter-btn {
  padding: 6px 16px;
  border: 1px solid #e5e7eb;
  border-radius: 20px;
  background: #fff;
  font-size: 13px;
  color: #4b5563;
  cursor: pointer;
  transition: all 0.2s ease;
  font-family: inherit;
}

.sub-filter-btn:hover {
  border-color: #4F46E5;
  color: #4F46E5;
}

.sub-filter-btn.active {
  background: #4F46E5;
  border-color: #4F46E5;
  color: #fff;
}

.block-products {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 24px;
  flex: 1;
}

.product-card-lg {
  background: #fff;
  border-radius: 14px;
  overflow: hidden;
  border: 1px solid #eef2f6;
  transition: all 0.3s ease;
  cursor: pointer;
  display: flex;
  flex-direction: column;
}

.product-card-lg:hover {
  transform: translateY(-6px);
  box-shadow: 0 12px 32px rgba(10, 10, 26, 0.08);
  border-color: #4F46E5;
}

.product-card-lg .card-img-wrap {
  height: 180px;
  background: #f8fafc;
  position: relative;
  overflow: hidden;
  flex-shrink: 0;
}

.product-card-lg .card-img {
  width: 100%;
  height: 100%;
}

.product-card-lg .card-badge {
  position: absolute;
  top: 8px;
  left: 8px;
  background: rgba(10, 10, 26, 0.85);
  color: #fff;
  font-size: 11px;
  padding: 3px 14px;
  border-radius: 100px;
}

.product-card-lg .card-body {
  padding: 18px 16px 14px;
  flex: 1;
  display: flex;
  flex-direction: column;
}

.product-card-lg .card-name {
  font-size: 17px;
  font-weight: 600;
  color: #0a0a1a;
  margin: 0 0 4px 0;
}

.product-card-lg .card-model {
  font-size: 14px;
  color: #4F46E5;
  font-weight: 500;
  margin-bottom: 10px;
}

.product-card-lg .card-desc {
  font-size: 13px;
  color: #6b7280;
  line-height: 1.5;
  margin: 4px 0 8px 0;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  text-overflow: ellipsis;
}

.product-card-lg .card-specs {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  margin-bottom: 12px;
}

.product-card-lg .spec-tag {
  font-size: 12px;
  background: #f3f4f6;
  padding: 2px 12px;
  border-radius: 100px;
  color: #6b7280;
}

.product-card-lg .card-footer {
  display: flex;
  justify-content: flex-end;
  padding-top: 10px;
  border-top: 1px dashed #f0f2f5;
  margin-top: auto;
}

.product-card-lg .card-footer .el-button {
  font-size: 13px;
}

.empty-product-tip {
  grid-column: 1 / -1;
  text-align: center;
  color: #9ca3af;
  padding: 40px 0;
  font-size: 15px;
}

.load-more-wrap {
  display: flex;
  justify-content: center;
  padding: 20px 0 8px 0;
}

/* ========== 特色推荐区 ========== */
.featured-section {
  max-width: 100%;
  margin: 0 auto 0;
  padding: 0 64px 72px;
}

.featured-inner {
  background: #0a0a1a;
  border-radius: 32px;
  padding: 56px 64px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 48px;
  overflow: hidden;
  position: relative;
}

.featured-info {
  flex: 1;
  color: #fff;
  z-index: 2;
}

.featured-label {
  display: flex;
  align-items: center;
  gap: 10px;
  font-size: 13px;
  font-weight: 500;
  color: rgba(255, 255, 255, 0.5);
  letter-spacing: 0.5px;
  text-transform: uppercase;
  margin-bottom: 14px;
}

.label-line {
  width: 28px;
  height: 2px;
  background: #4F46E5;
  border-radius: 2px;
}

.featured-info h2 {
  font-size: 32px;
  font-weight: 700;
  margin: 0 0 10px 0;
  letter-spacing: -0.5px;
}

.featured-info p {
  font-size: 16px;
  color: rgba(255, 255, 255, 0.6);
  line-height: 1.8;
  margin: 0 0 18px 0;
}

.featured-features {
  list-style: none;
  padding: 0;
  margin: 0 0 28px 0;
  display: flex;
  flex-wrap: wrap;
  gap: 12px 20px;
}

.featured-features li {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 14px;
  color: rgba(255, 255, 255, 0.7);
}

.featured-features li .el-icon {
  color: #34d399;
  font-size: 16px;
}

.featured-btn {
  display: inline-flex;
  align-items: center;
  gap: 10px;
  padding: 12px 32px;
  border-radius: 100px;
  border: none;
  background: #fff;
  color: #0a0a1a;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  font-family: inherit;
}

.featured-btn:hover {
  transform: translateY(-2px);
  box-shadow: 0 8px 32px rgba(255, 255, 255, 0.15);
}

.featured-visual {
  flex: 0 0 180px;
  height: 180px;
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
}

.featured-glow {
  position: absolute;
  width: 220px;
  height: 220px;
  border-radius: 50%;
  background: radial-gradient(circle at center, rgba(79, 70, 229, 0.2), transparent 70%);
  animation: pulse-glow 4s ease-in-out infinite;
}

@keyframes pulse-glow {
  0%, 100% { transform: scale(1); opacity: 0.8; }
  50% { transform: scale(1.2); opacity: 1; }
}

.featured-icon-wrap {
  position: relative;
  z-index: 2;
  width: 120px;
  height: 120px;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.06);
  border: 1px solid rgba(255, 255, 255, 0.08);
  display: flex;
  align-items: center;
  justify-content: center;
  color: rgba(255, 255, 255, 0.15);
}

/* ========== 响应式 ========== */
@media (max-width: 1200px) {
  .category-sections-wrapper {
    flex-direction: column;
    padding: 0 20px 32px;
  }

  .filter-sidebar-sticky {
    width: 100%;
    position: static;
    margin-bottom: 16px;
  }

  .filter-card {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 12px;
    padding: 12px 16px;
  }

  .filter-title {
    margin-bottom: 0;
    padding-bottom: 0;
    border-bottom: none;
    font-size: 14px;
    margin-right: 12px;
  }

  .filter-list {
    flex-direction: row;
    flex-wrap: wrap;
    gap: 6px;
  }

  .filter-item {
    padding: 4px 12px;
    width: auto;
    font-size: 13px;
  }

  .block-header {
    width: 220px;
    min-width: 220px;
    min-height: 320px;
    padding: 28px 20px;
  }

  .block-products {
    grid-template-columns: repeat(2, 1fr);
    gap: 16px;
  }

  .product-card-lg .card-img-wrap {
    height: 150px;
  }
}

@media (max-width: 768px) {
  .hero-section {
    flex-direction: column;
    padding: 40px 20px 32px;
    gap: 40px;
    min-height: auto;
  }
  .block-header {
    padding: 28px 20px;
    justify-content: flex-start;
  }

  .hero-title {
    font-size: 40px;
  }

  .hero-visual {
    flex: 0 0 280px;
    height: 280px;
  }

  .search-bar-wrapper {
    padding: 12px 16px 0;
  }

  .search-bar-inner {
    flex-direction: column;
    padding: 12px;
  }

  .search-bar-inner .el-button {
    width: 100%;
    justify-content: center;
  }

  .category-sections-wrapper {
    padding: 0 16px 24px;
  }

  .category-block {
    flex-direction: column;
    gap: 0;
  }

  .block-header {
    width: 100%;
    min-width: unset;
    min-height: 200px;
    padding: 24px 20px;
  }

  .block-title {
    font-size: 24px;
  }

  .block-desc {
    max-width: 100%;
  }

  .block-right {
    padding: 16px 16px 20px;
  }

  .block-products {
    grid-template-columns: repeat(2, 1fr);
    gap: 12px;
  }

  .product-card-lg .card-img-wrap {
    height: 130px;
  }

  .product-card-lg .card-name {
    font-size: 15px;
  }

  .product-card-lg .card-model {
    font-size: 13px;
  }
}

@media (max-width: 480px) {
  .block-products {
    grid-template-columns: 1fr 1fr;
    gap: 10px;
  }

  .product-card-lg .card-img-wrap {
    height: 110px;
  }

  .product-card-lg .card-body {
    padding: 12px 10px;
  }

  .product-card-lg .card-name {
    font-size: 13px;
  }

  .product-card-lg .card-model {
    font-size: 12px;
  }

  .product-card-lg .spec-tag {
    font-size: 10px;
    padding: 0 8px;
  }
}
</style>